import {
  getTrendSummary, getTrendSeries, getTrendTransitions, getTrendUnits, getTrendEmployees,
  getScaleOptions, getScaleMappings,
} from '../../../services/performanceTrendsService';
import { EMPTY_FILTERS, toAnalyticsParams } from '../../../components/performance/pmAnalyticsFilters';

// PM analitik P6 — E11 Dönemler arası trendler veri katmanı. Ortak F0 süzgeçleri pmAnalyticsFilters'ın URL durumundadır;
// bu ekrana özgü durum (karşılaştırılan dönemler, popülasyon modu, aynı rol, sekme, geçiş hücresi) aynı sorgu dizesinde
// kendi anahtarlarıyla tutulur (detaydan geri dönüşte korunur).

export const MIN_PERIODS = 2;
export const MAX_PERIODS = 8;
export const DEFAULT_PERIOD_COUNT = 3;
export const UNIT_PANEL_ROWS = 6;
export const BANDS = ['high', 'mid', 'low'];
export const MODES = ['cohort', 'population'];
export const TABS = ['summary', 'units', 'employees'];

// E03 sonuç dağılımı ekranı (veri noktası → dönem E03). Entegrasyonda bağlanır; null iken nokta tıklaması o dönemin
// çalışan listesine gider.
export const RESULTS_PATH = '/performance/results';

const Q = { periods: 'periods', mode: 'mode', role: 'role', tab: 'tab', fromBand: 'fb', toBand: 'tb' };
const list = (v) => (v ? v.split(',').map((x) => x.trim()).filter(Boolean) : []);

export const EMPTY_TREND = Object.freeze({
  periods: [], mode: 'cohort', sameRole: false, tab: 'summary', fromBand: null, toBand: null,
});

export function parseTrendState(search) {
  const p = new URLSearchParams(search || '');
  const mode = p.get(Q.mode);
  const tab = p.get(Q.tab);
  const fb = p.get(Q.fromBand);
  const tb = p.get(Q.toBand);
  const bandsOk = BANDS.includes(fb) && BANDS.includes(tb);
  return {
    periods: [...new Set(list(p.get(Q.periods)))].slice(0, MAX_PERIODS),
    mode: MODES.includes(mode) ? mode : 'cohort',
    sameRole: p.get(Q.role) === '1',
    tab: TABS.includes(tab) ? tab : 'summary',
    fromBand: bandsOk ? fb : null,
    toBand: bandsOk ? tb : null,
  };
}

// F0 anahtarları (pmAnalyticsFilters) ve diğerleri korunur; yalnız bu ekranın anahtarları yeniden yazılır.
export function serializeTrendState(state, baseSearch = '') {
  const p = new URLSearchParams(baseSearch || '');
  Object.values(Q).forEach((k) => p.delete(k));
  const s = { ...EMPTY_TREND, ...state };
  if (s.periods.length) p.set(Q.periods, s.periods.join(','));
  if (s.mode !== 'cohort') p.set(Q.mode, s.mode);
  if (s.sameRole) p.set(Q.role, '1');
  if (s.tab !== 'summary') p.set(Q.tab, s.tab);
  if (s.fromBand && s.toBand) { p.set(Q.fromBand, s.fromBand); p.set(Q.toBand, s.toBand); }
  const out = p.toString();
  return out ? `?${out}` : '';
}

const dayOf = (v) => (v ? String(v).slice(0, 10) : '');

/** Dönemleri kronolojik sıralar (başlangıç, sonra bitiş). */
export function chronological(periods) {
  return [...(periods || [])].sort((a, b) => dayOf(a.startDate).localeCompare(dayOf(b.startDate))
    || dayOf(a.endDate).localeCompare(dayOf(b.endDate)));
}

/**
 * Varsayılan seçim: en son kapanmış (yerli ya da içe aktarılmış) en çok 3 dönem; kapanmış dönem 2'den azsa en yeni başlayan 2
 * dönem. Trendler yayımlanmış finali kullandığından kapanmış dönemler önceliklidir.
 */
export function defaultTrendPeriods(periods, keep = []) {
  const all = periods || [];
  const kept = keep.filter((id) => all.some((p) => p.id === id));
  const closed = chronological(all.filter((p) => p.status === 'closed' && !kept.includes(p.id)));
  const pool = closed.length + kept.length >= MIN_PERIODS ? closed : chronological(all.filter((p) => !kept.includes(p.id)));
  const need = Math.max(MIN_PERIODS, DEFAULT_PERIOD_COUNT) - kept.length;
  const picked = [...kept, ...(need > 0 ? pool.slice(-need) : []).map((p) => p.id)];
  // Bağlantıyla gelen (ör. içe aktarma listesinden "Trendde Göster") dönem korunur; seçim kronolojik.
  return chronological(all.filter((p) => picked.includes(p.id))).map((p) => p.id);
}

/** Uç parametreleri: F0 süzgeçleri (dönem ve katılımcı durumu hariç) + periodIds/mode/sameRole. */
export function trendParams(filters, state) {
  const base = toAnalyticsParams({ ...EMPTY_FILTERS, ...filters, participantStatuses: [], comparePeriodId: null }, null);
  const params = { ...base, periodIds: (state.periods || []).join(',') };
  if (state.mode && state.mode !== 'cohort') params.mode = state.mode;
  if (state.sameRole) params.sameRole = 'true';
  return params;
}

export class PanelError extends Error {}

const payload = (res) => (res?.data?.status ? res.data.data : undefined);

async function unwrap(promise) {
  const res = await promise;
  const data = payload(res);
  if (data === undefined) throw new PanelError(res?.data?.message || 'error');
  return data;
}

export const fetchSummary = (params) => unwrap(getTrendSummary(params));
export const fetchSeries = (params) => unwrap(getTrendSeries(params));
export const fetchTransitions = (params) => unwrap(getTrendTransitions(params));
export const fetchUnits = (params) => unwrap(getTrendUnits(params));
export const fetchEmployees = (params, { pageNumber, pageSize, fromBand, toBand }) => unwrap(getTrendEmployees({
  ...params, pageNumber, pageSize, ...(fromBand && toBand ? { fromBand, toBand } : {}),
}));
export const fetchScaleOptions = () => unwrap(getScaleOptions());
export const fetchScaleMappings = () => unwrap(getScaleMappings());

// ---------------------------------------------------------------- görüntü yardımcıları
export const hasNum = (v) => v !== null && v !== undefined && !Number.isNaN(Number(v));
export const fmt = (v, lang, digits = 2) => (hasNum(v) ? new Intl.NumberFormat(lang, { maximumFractionDigits: digits }).format(Number(v)) : '');

/** İşaretli fark metni: puan farkı ("+0,16 Puan") ya da yüzde puan ("−16 YP"). */
export function signed(v, lang) {
  if (!hasNum(v)) return '';
  const n = Number(v);
  return `${n > 0 ? '+' : ''}${fmt(n, lang)}`;
}

/** Karşılaştırma tabanına göre dönem değeri: doğal ölçekte ortalama final, onaylı eşlemede M60, karşılaştırılamazsa null. */
export function comparableValue(point, basis) {
  if (!point) return null;
  if (basis === 'normalized') return point.normalized?.value ?? null;
  if (basis === 'native') return point.final?.value ?? null;
  return null;
}

/** Grafik satırları: dönem başına final (seçili tabanda), hedef ve yetkinlik ortalaması + n. */
export function seriesRows(points, basis) {
  return (points || []).map((p) => ({
    key: p.period.periodId,
    name: p.period.name,
    final: comparableValue(p, basis),
    goal: p.goal?.value ?? null,
    competency: p.competency?.value ?? null,
    n: p.finalN,
    eligible: p.eligibleN,
    suppressed: !!(p.final?.suppressionReason || p.goal?.suppressionReason),
    closed: p.period.closed,
    imported: p.period.origin === 'imported',
  }));
}

/** Geçiş matrisi hücresi. */
export const matrixCell = (matrix, from, to) => (matrix?.cells || []).find((c) => c.fromBand === from && c.toBand === to) || null;

/** Bulgu parametrelerini biçimlendirir (sayılar dile göre). */
export function insightParams(insight, lang) {
  const out = {};
  Object.entries(insight?.params || {}).forEach(([k, v]) => {
    out[k] = typeof v === 'number' ? fmt(v, lang) : (v ?? '');
  });
  return out;
}

export const metricUnitSuffix = (change, t) => (change?.unit === 'pp' ? t('performance.trends.unit.pp') : t('performance.trends.unit.score'));
